import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Users, Search, X } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import UploadModal from "@/components/UploadModal";
import LibraryCreateHero from "@/components/library/LibraryCreateHero";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMyLibrariesQuery, useMyLibrariesCountsQuery, useMyDataQuery, useCommunityChannelsQuery, MyProject, MyLibrary } from "@/generated/graphql";
import { useWindowScroll } from "@uidotdev/usehooks";
import { ProjectCard } from "@/components/ProjectCard";
import CopyLibraryDialog from "@/components/CopyLibraryDialog";
import { useUrlUpload } from "@/hooks/use-url-upload";
import { useToast } from "@/hooks/use-toast";
import { useUploadFile, UploadProgress } from "@/hooks/use-upload-file";
import { useQuotaUpsellDialog } from "@/hooks/use-quota-upsell-dialog";
import { useHasFeature } from "@/lib/useHasFeature";
import { VideoMetadata } from "@/lib/video-metadata";
import { classifyLibraryOrigin } from "@/lib/library/classifyLibraryOrigin";
import { computeLibraryRefreshVariables } from "@/lib/library/computeLibraryRefreshVariables";
import { listUserLibraries } from "@/lib/library/listUserLibraries";
import { pickCommunityPreviewAvatars } from "@/lib/library/pickCommunityPreviewAvatars";
import { isPaidSubscription } from "@/lib/quota/isPaidSubscription";
import { detectAudioTrack } from "@/lib/media/detectAudioTrack";

const PAGE_SIZE = 12;

const LibraryListPage = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, loading, error, fetchMore, refetch } = useMyLibrariesQuery({
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
  });
  const { data: myData, refetch: refetchMyData } = useMyDataQuery();
  // Authoritative totals from the backend. The loaded `myLibraries` page is
  // paginated, so counting it client-side under-reports until every page has
  // scrolled in. These counts cover the user's full library.
  const { data: countsData, refetch: refetchCounts } = useMyLibrariesCountsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const { data: communityChannelsData } = useCommunityChannelsQuery({
    fetchPolicy: 'cache-and-network',
  });
  const isPaidPlan = useMemo(
    () => isPaidSubscription(myData?.myData?.subscription),
    [myData?.myData?.subscription],
  );
  const hasProFeatures = useHasFeature('pro_features');
  const { dialog: quotaUpsellDialog, openQuotaUpsell } = useQuotaUpsellDialog();
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const isFetchingMoreRef = useRef(false);
  const [scroll] = useWindowScroll();
  const scrollY = scroll?.y ?? 0;

  const [searchQuery, setSearchQuery] = useState("");
  const [duplicateLibrary, setDuplicateLibrary] = useState<MyLibrary | null>(null);

  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { getUploadUrl, error: uploadError, createBlobUrl, revokeBlobUrl, getVideoDuration, abortUpload } = useUploadFile();
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadedVideoUrl, setUploadedVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const {
    videoUrl,
    setVideoUrl,
    urlVideoMetadata,
    isLoadingMetadata,
    communityMatch, setCommunityMatch,
    handleUrlUpload, resetUpload, navigateToCommunityMatch,
  } = useUrlUpload({
    maxVideoSeconds: myData?.myData?.subscription?.plan?.max_library_video_seconds,
    hasProAccess: hasProFeatures,
    onProRequired: () => openQuotaUpsell('paid_feature'),
  });

  // Users routed here from the marketing site (directly, or bounced from
  // /projects by the objective redirect) arrive with the pasted link in ?url=.
  // Prefill + open the create modal, then drop the param to avoid re-triggering.
  const urlParamConsumed = useRef(false);
  useEffect(() => {
    if (urlParamConsumed.current) return;
    const incomingUrl = searchParams.get("url");
    if (!incomingUrl) return;
    urlParamConsumed.current = true;
    setVideoUrl(incomingUrl);
    handleUrlUpload(incomingUrl);
    const next = new URLSearchParams(searchParams);
    next.delete("url");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (searchParams.get('focus') !== 'video-url-input') return;

    const input = document.getElementById('video-url-input');
    if (input) {
      input.focus();
      input.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    setSearchParams((prev) => {
      prev.delete('focus');
      return prev;
    }, { replace: true });
  }, [searchParams, setSearchParams]);

  const libraries = useMemo(() => data?.myLibraries ?? [], [data?.myLibraries]);

  // Tracks how many libraries are currently loaded (incl. paginated pages) so the
  // poll below can refresh the full range without resetting the interval timer.
  const loadedCountRef = useRef(libraries.length);
  loadedCountRef.current = libraries.length;

  const visibleLibraries = useMemo(
    () => listUserLibraries(libraries, { search: searchQuery }),
    [libraries, searchQuery],
  );
  const communityPreview = useMemo(
    () => pickCommunityPreviewAvatars(communityChannelsData?.communityChannels ?? []),
    [communityChannelsData?.communityChannels],
  );

  // Prefer the authoritative backend totals; fall back to the loaded subset
  // while the counts query is still in flight.
  const mineCount = countsData?.myLibrariesCounts?.mine
    ?? libraries.filter((library) => classifyLibraryOrigin(library) === 'uploaded').length;
  const communityCount = countsData?.myLibrariesCounts?.community
    ?? libraries.filter((library) => classifyLibraryOrigin(library) === 'community').length;
  const isFirstClip = mineCount + communityCount === 0;

  const handleGenerate = () => {
    const url = videoUrl.trim();
    if (!url) {
      toast({
        title: t('library.urlRequired'),
        description: t('library.urlRequiredDescription'),
      });
      return;
    }
    handleUrlUpload(url);
  };

  const handleUploadFile = () => {
    if (!hasProFeatures) {
      openQuotaUpsell('paid_feature');
      return;
    }
    fileInputRef.current?.click();
  };

  const cleanupBlobUrl = () => {
    if (blobUrl) revokeBlobUrl(blobUrl);
    setBlobUrl(null);
    setSelectedFile(null);
    setUploadProgress(null);
    setUploadedVideoUrl(null);
    setIsUploadComplete(false);
    setIsUploaded(false);
    setVideoDuration(0);
  };

  const startUpload = async (file: File) => {
    try {
      setUploadProgress({ percentage: 0, uploadedBytes: 0, totalBytes: file.size, uploadSpeed: 0, remainingTime: 0 });
      const uploadUrl = await getUploadUrl(file, (progress) => {
        setUploadProgress(progress);
      });
      if (uploadUrl) {
        setUploadedVideoUrl(uploadUrl);
        setIsUploadComplete(true);
        setUploadProgress(null);
        setIsUploaded(true);
      }
    } catch (error) {
      if (!(error instanceof Error && error.message === 'Upload was aborted')) {
        toast({
          title: t('errors.uploadError'),
          description: uploadError || t('errors.uploadErrorOccurred'),
          variant: "destructive",
        });
      }
      setUploadProgress(null);
      setIsUploadComplete(false);
    }
  };

  const handleFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.type !== 'video/mp4') {
      toast({
        title: t('errors.invalidFileType'),
        description: t('errors.onlyMp4Accepted'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    let duration: number | null = null;
    try {
      duration = await getVideoDuration(file);
    } catch (error) {
      console.error('Error getting video duration:', error);
    }
    if (!duration) {
      toast({
        title: t('errors.failedToReadVideo'),
        description: t('errors.couldNotDetermineDuration'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // A video without audio cannot be transcribed, so processing would fail
    if (await detectAudioTrack(file) === 'absent') {
      toast({
        title: t('errors.videoHasNoAudio'),
        description: t('errors.videoHasNoAudioDescription'),
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setUploadProgress(null);
    setIsUploadComplete(false);
    setUploadedVideoUrl(null);
    setVideoDuration(duration);
    setBlobUrl(createBlobUrl(file));
    setSelectedFile(file);
    setIsUploadModalOpen(true);
    startUpload(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const fileVideoMetadata: VideoMetadata | null = useMemo(() => {
    if (urlVideoMetadata || !blobUrl || !selectedFile) return null;
    return {
      url: isUploadComplete && uploadedVideoUrl ? uploadedVideoUrl : blobUrl,
      title: selectedFile.name.replace(/\.[^/.]+$/, ''),
      duration: videoDuration,
      platform: 'http',
      thumbnail: undefined,
      best_video_format: {
        format_id: 'upload',
        ext: 'mp4',
        url: blobUrl,
        has_audio: true,
        has_video: true,
      },
    };
  }, [urlVideoMetadata, blobUrl, selectedFile, videoDuration, isUploadComplete, uploadedVideoUrl]);

  const handleModalClose = () => {
    if (urlVideoMetadata) {
      resetUpload();
      return;
    }
    if (uploadProgress && !isUploadComplete) abortUpload();
    setIsUploadModalOpen(false);
    cleanupBlobUrl();
  };

  const handleLoadMore = async () => {
    if (isFetchingMoreRef.current || loading || error || !hasMore) return;
    isFetchingMoreRef.current = true;
    setIsFetchingMore(true);
    try {
      const prevCount = libraries.length;
      const res = await fetchMore({
        variables: { limit: PAGE_SIZE, offset: prevCount },
      });
      const fetched = res.data?.myLibraries?.length ?? 0;
      if (fetched < PAGE_SIZE) setHasMore(false);
    } finally {
      isFetchingMoreRef.current = false;
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {
    if (loading || !hasMore) return;
    const currentY = scrollY ?? 0;
    const viewportH = window.innerHeight || document.documentElement.clientHeight;
    const docH = document.documentElement.scrollHeight || document.body.scrollHeight;
    const distanceFromBottom = docH - (currentY + viewportH);
    if (distanceFromBottom < 600) {
      handleLoadMore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollY, hasMore, loading]);

  useEffect(() => {
    if (!loading && libraries.length > 0 && libraries.length < PAGE_SIZE) {
      setHasMore(false);
    }
  }, [loading, libraries.length]);

  // Poll for processing libraries
  const hasProcessingLibraries = useMemo(() => {
    return libraries.some((lib) => !['success', 'failure', 'expired'].includes(lib.curation_status));
  }, [libraries]);

  useEffect(() => {
    if (!hasProcessingLibraries) return;
    const pollInterval = setInterval(() => {
      // Refetch the full loaded range, not just page 1 — refetch()'s default
      // (offset 0, limit PAGE_SIZE) would hit the merge function's offset-0
      // "replace" branch and truncate paginated items out of the list.
      refetch(computeLibraryRefreshVariables({ loadedCount: loadedCountRef.current, pageSize: PAGE_SIZE }));
      // Keep the empty-state totals in sync as libraries finish processing or fail out.
      refetchCounts();
    }, 10000);
    return () => clearInterval(pollInterval);
  }, [hasProcessingLibraries, refetch, refetchCounts]);

  return (
    <div className="relative min-h-[100dvh] bg-background p-4 lg:p-6">
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-80"
        style={{ background: 'radial-gradient(ellipse 70% 280px at 50% 0%, hsl(var(--primary) / 0.09) 0%, transparent 100%)' }}
      />
      <div
        data-testid="library-page-stack"
        className={`relative w-full max-w-screen-2xl mx-auto ${isFirstClip ? 'flex min-h-[calc(100dvh-5.5rem)] flex-col justify-center py-4' : 'space-y-5 lg:space-y-6'}`}
      >
        <LibraryCreateHero
          videoUrl={videoUrl}
          isLoadingMetadata={isLoadingMetadata}
          isFirstClip={isFirstClip}
          compact={!isFirstClip}
          communityAvatars={communityPreview.avatars}
          communityChannelCount={communityPreview.totalCount}
          onVideoUrlChange={setVideoUrl}
          onGenerate={handleGenerate}
          onUploadFile={handleUploadFile}
        />

        {!isFirstClip && (
        <div className="space-y-3">
          <div>
            <h2 id="library-project-history" className="text-lg sm:text-xl font-bold">{t('library.projectHistory')}</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              {t('library.projectHistoryDescription')}
            </p>
          </div>

          {/* Search Bar */}
          <div className="space-y-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('library.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-8"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

          </div>

          {/* Library Items Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading && Array.from({ length: 8 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="p-3 sm:p-4 animate-pulse">
                <div className="aspect-video bg-muted rounded-lg mb-3" />
                <div className="h-3 bg-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-muted rounded w-full mb-1" />
                <div className="h-3 bg-muted rounded w-2/3" />
              </Card>
            ))}

            {!loading && !error && visibleLibraries.map((library) => (
              <ProjectCard
                key={library.id}
                project={library as unknown as MyProject}
                type="library"
                origin={classifyLibraryOrigin(library)}
                onDuplicate={() => setDuplicateLibrary(library as unknown as MyLibrary)}
              />
            ))}

            {!loading && !error && visibleLibraries.length === 0 && (
              <div className="col-span-full text-center text-sm text-muted-foreground py-8">
                <p>{t('library.noItemsFound')}</p>
              </div>
            )}
            {error && (
              <div className="col-span-full text-center text-sm text-destructive">
                {t('library.failedToLoad')}
              </div>
            )}
            {isFetchingMore && (
              <div className="col-span-full text-center text-xs text-muted-foreground py-2">{t('library.loadingMore')}</div>
            )}
          </div>
        </div>
        )}
      </div>

      {/* Upload Modal */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".mp4,video/mp4"
        className="hidden"
        onChange={handleFileSelection}
      />
      <UploadModal
        isOpen={!!urlVideoMetadata || isUploadModalOpen}
        onClose={handleModalClose}
        onUploadComplete={() => {
          resetUpload();
          setIsUploadModalOpen(false);
          cleanupBlobUrl();
          refetchMyData();
          refetchCounts();
        }}
        videoMetadata={urlVideoMetadata ?? fileVideoMetadata}
        uploadProgress={uploadProgress}
        isUploadComplete={urlVideoMetadata ? true : isUploadComplete}
        isUploaded={urlVideoMetadata ? false : isUploaded}
        mode="library"
      />
      {quotaUpsellDialog}

      <CopyLibraryDialog
        isOpen={!!duplicateLibrary}
        onClose={() => setDuplicateLibrary(null)}
        title={t('projectCard.duplicateProject')}
        isPaidPlan={isPaidPlan}
        library={duplicateLibrary ? {
          name: duplicateLibrary.name,
          // Reconstruct YouTube watch URLs for user uploads so duplicates
          // still submit a provider URL instead of an S3 object.
          base_video_url: duplicateLibrary.youtube_video_id
            ? `https://www.youtube.com/watch?v=${duplicateLibrary.youtube_video_id}`
            : duplicateLibrary.base_video_url,
          duration_seconds: duplicateLibrary.duration_seconds,
          format: duplicateLibrary.format,
          language: duplicateLibrary.language,
          audio_language: duplicateLibrary.audio_language,
          audio_track: duplicateLibrary.audio_track,
          start_time: duplicateLibrary.start_time,
          end_time: duplicateLibrary.end_time,
          source_library_id: duplicateLibrary.source_library_id ?? null,
        } : null}
      />

      {/* Community Library Available Dialog */}
      <Dialog open={!!communityMatch} onOpenChange={(open) => {
        if (!open) {
          setCommunityMatch(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('library.communityAvailableTitle')}</DialogTitle>
            <DialogDescription>
              {t('library.communityAvailableDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button
              variant="gradient"
              onClick={navigateToCommunityMatch}
            >
              <Users className="w-4 h-4 mr-2" />
              {t('library.goToCommunityLibrary')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LibraryListPage;
